import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface SHIScoreProps {
  rows: RawIndicatorRow[];
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const toNum = (v: string | number | undefined): number => {
  if (v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmtNum = (n: number): string => n.toLocaleString();

// ─── data parsing ─────────────────────────────────────────────────────────────

interface CaseRiskData {
  totalOpen: number;
  riskyTotal: number;
  veryHigh: number;
  high: number;
  medium: number;
  lowRisk: number;
  avgRiskScore?: number;
  globalMax?: number;
  productScore?: number;
}

function parseSHIData(rows: RawIndicatorRow[]): CaseRiskData {
  let totalOpen = 0;
  let riskyTotal = 0;
  let veryHigh = 0;
  let high = 0;
  let medium = 0;
  let lowRisk = 0;
  let avgRiskScore: number | undefined;
  let globalMax: number | undefined;
  let productScore: number | undefined;

  for (const row of rows) {
    const metric = (row.metricName ?? "").trim();
    const factor = (row.factorLabel ?? "").trim();
    const val = toNum(row.value);

    if (metric === "Total Open" && factor === "") {
      totalOpen = val;
    } else if (metric === "Risky Cases (used in score)") {
      if (factor === "") {
        riskyTotal = val;
      } else if (factor === "Very High") {
        veryHigh = val;
      } else if (factor === "High") {
        high = val;
      } else if (factor === "Medium") {
        medium = val;
      }
    } else if (metric === "Low Risk (not in score)" && factor === "") {
      lowRisk = val;
    } else if (metric === "Avg Risk Score") {
      avgRiskScore = val;
    } else if (metric === "GlobalMax") {
      globalMax = val;
    } else if (metric === "ProductScore") {
      productScore = val;
    }
  }

  return { totalOpen, riskyTotal, veryHigh, high, medium, lowRisk, avgRiskScore, globalMax, productScore };
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface CountBoxProps {
  label: string;
  value: number;
  accent?: string;
  bg?: string;
  border?: string;
  note?: string;
  bold?: boolean;
}

const CountBox: React.FC<CountBoxProps> = ({ label, value, accent = "#323130", bg = "#FFFFFF", border = "#EDEBE9", note, bold }) => (
  <Stack
    styles={{
      root: {
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        padding: "12px 16px",
        minWidth: 110,
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      },
    }}
    horizontalAlign="center"
    verticalAlign="center"
    tokens={{ childrenGap: 2 }}
  >
    <Text variant="small" styles={{ root: { color: accent, fontWeight: 600, textAlign: "center", lineHeight: "16px" } }}>
      {label}
    </Text>
    <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: accent, lineHeight: "36px" } }}>
      {fmtNum(value)}
    </Text>
    {note && (
      <Text variant="xSmall" styles={{ root: { color: "#A19F9D", textAlign: "center" } }}>
        {note}
      </Text>
    )}
    {bold && (
      <Text variant="xSmall" styles={{ root: { color: accent, fontWeight: 600 } }}>
        ▲ used in score
      </Text>
    )}
  </Stack>
);

interface SeverityBoxProps {
  label: string;
  value: number;
  accent: string;
  bg: string;
  border: string;
}

const SeverityBox: React.FC<SeverityBoxProps> = ({ label, value, accent, bg, border }) => (
  <Stack
    horizontalAlign="center"
    verticalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: bg,
        border: `1.5px solid ${border}`,
        borderRadius: 8,
        padding: "10px 20px",
        minWidth: 100,
        flex: "1 1 0",
      },
    }}
  >
    <Text variant="medium" styles={{ root: { color: accent, fontWeight: 700, textAlign: "center" } }}>
      {label}
    </Text>
    <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: accent, lineHeight: "36px" } }}>
      {fmtNum(value)}
    </Text>
  </Stack>
);

// ─── main export ──────────────────────────────────────────────────────────────

const SHIScoreRawIndicatorCards: React.FC<SHIScoreProps> = ({ rows }) => {
  const data = useMemo(() => parseSHIData(rows), [rows]);

  return (
    <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: "flex-start" } }}>

      {/* ── Case Risk Profile ── */}
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{
          root: {
            backgroundColor: "#FFFBF0",
            border: "1.5px solid #FFB900",
            borderRadius: 12,
            padding: "20px 24px",
            minWidth: 380,
            maxWidth: 520,
            flex: "1 1 380px",
          },
        }}
      >
        {/* header */}
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Icon iconName="Warning" styles={{ root: { fontSize: 18, color: "#FFB900" } }} />
          <Text variant="large" styles={{ root: { fontWeight: 700, color: "#C15A00" } }}>
            Case Risk Profile
          </Text>
        </Stack>

        {/* example note */}
        <Text variant="small" styles={{ root: { color: "#605E5C", marginTop: -8 } }}>
          Proactive support case analysis for this tenant.
        </Text>

        {/* top row: Total Open · Risky Cases · Low Risk */}
        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { flexWrap: "wrap" } }}>
          <CountBox
            label="Total Open"
            value={data.totalOpen}
            bg="#FFFFFF"
            border="#EDEBE9"
          />
          <CountBox
            label="⚠ Risky Cases (used in score)"
            value={data.riskyTotal}
            accent="#D13438"
            bg="#FDF3F3"
            border="#D13438"
          />
          <CountBox
            label="Low Risk"
            value={data.lowRisk}
            accent="#107C10"
            bg="#F1FBF1"
            border="#107C10"
            note="not in score"
          />
        </Stack>

        {/* breakdown triangle header */}
        <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="medium" styles={{ root: { fontWeight: 600, color: "#C15A00" } }}>
            ▼ breakdown of {fmtNum(data.riskyTotal)} Risky Cases
          </Text>

          <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: "100%" } }}>
            <SeverityBox
              label="Very High"
              value={data.veryHigh}
              accent="#D13438"
              bg="#FDF3F3"
              border="#D13438"
            />
            <SeverityBox
              label="High"
              value={data.high}
              accent="#C15A00"
              bg="#FFF7F0"
              border="#C15A00"
            />
            <SeverityBox
              label="Medium"
              value={data.medium}
              accent="#FFB900"
              bg="#FFFBF0"
              border="#FFB900"
            />
          </Stack>

          {/* sum equation */}
          <Text variant="smallPlus" styles={{ root: { color: "#605E5C" } }}>
            {fmtNum(data.veryHigh)} + {fmtNum(data.high)} + {fmtNum(data.medium)}
            {" = "}
            <Text variant="smallPlus" styles={{ root: { fontWeight: 700, color: "#D13438" } }}>
              {fmtNum(data.riskyTotal)} Risky Cases
            </Text>
          </Text>
        </Stack>

        {/* divider */}
        <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />

        {/* hierarchy note */}
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { color: "#323130" } }}>
            <strong>Hierarchy:</strong> Total Open Cases = Risky Cases + Low Risk (non-risky).
          </Text>
          <Text variant="small" styles={{ root: { color: "#323130" } }}>
            Risky Cases breakdown: Very High + High + Medium.
          </Text>
          <Text variant="small" styles={{ root: { color: "#D13438" } }}>
            ⚠ <strong>Low Risk cases are NOT part of the SHI Proactive Score calculation.</strong>{" "}
            Only Risky cases (Very High, High, Medium) contribute to Proactive Support Health Score.
          </Text>
        </Stack>
      </Stack>

      {/* Score Derivation panel — hidden */}
      {false && null}
    </Stack>
  );
};

export default SHIScoreRawIndicatorCards;
